import React, { useEffect, useMemo, useRef, useState } from 'react'
import useBookingSelection from './useBookingSelection'
import { DateTime } from 'luxon'
import { getOrdinalSuffix } from '../../utilities/dateUtils'
import { ChevronLeftIcon, ChevronRightIcon, XIcon, CalendarIcon } from "@heroicons/react/outline"
import { TBookingType } from '../../interfaces'
import { Calendar } from 'react-date-range'
import './style.css'
import VariantsBlock from './VariantBlock'
import { IProductVariant } from 'shopify-api-node'
import LoadingSpinner from '../Shared/LoadingSpinner'

// Constants
const TRACK_GRID_CONFIGS = {
  MAIN_TRACK: 'grid-cols-4',
  JUNIOR_TRACK: 'grid-cols-4', 
  BATTLE_KART: 'grid-cols-5',
  MINI_TRACK: 'grid-cols-3',
  DEFAULT: 'grid-cols-3'
} as const

const TRACK_STYLES = {
  BATTLE_KART: {
    fontClass: 'add-grobold-font',
    activeBackground: 'bg-[#EA581A]',
    calendarClass: 'battle-kart'
  },
  DEFAULT: {
    fontClass: '',
    activeBackground: 'bg-gradient-3',
    calendarClass: ''
  }
} as const

// Types
type TrackStyles = typeof TRACK_STYLES.BATTLE_KART | typeof TRACK_STYLES.DEFAULT

// Utility functions
const getGridConfig = (track: TBookingType): string => 
  TRACK_GRID_CONFIGS[track as keyof typeof TRACK_GRID_CONFIGS] || TRACK_GRID_CONFIGS.DEFAULT

const getTrackStyles = (track: TBookingType): TrackStyles => 
  track === 'BATTLE_KART' ? TRACK_STYLES.BATTLE_KART : TRACK_STYLES.DEFAULT

const adjustDateForMonth = (date: Date, monthOffset: number): Date => {
  const newDate = new Date(date)
  newDate.setMonth(newDate.getMonth() + monthOffset)
  
  // Handle month overflow by setting to last day if needed
  const lastDayOfMonth = new Date(newDate.getFullYear(), newDate.getMonth() + 1, 0).getDate()
  if (newDate.getDate() > lastDayOfMonth) {
    newDate.setDate(lastDayOfMonth)
  }
  
  return newDate
}

const formatDateString = (date: Date): string => {
  const baseDate = date ? DateTime.fromJSDate(date) : DateTime.now()
  return baseDate.toFormat("ccc, d") + 
         getOrdinalSuffix(+baseDate.toFormat("d")) + 
         baseDate.toFormat(" LLLL kkkk")
}

interface Props { 
  track?: TBookingType, 
  selectedVariants?: IProductVariant[], 
  onClick?: (selectedVariants: string[], changedVariant: IProductVariant, added: boolean) => void, 
  className?: string 
  allSelectable?: boolean
}
const BookingSelection = ({ track = "MAIN_TRACK", selectedVariants: selectedVariantsValue, onClick, className, allSelectable }: Props) => {
  const { 
    loading,
    selectedDate, 
    filterOptions, 
    filteredVariants, 
    product, 
    onDateChange, 
    getTotalAvailableSeats, 
    handleFilterOptionClick 
  } = useBookingSelection({ track, allSelectable })
  const [selectedVariants, setSelectedVariants] = useState<string[]>([])
  
  const gridConfig = getGridConfig(track)
  const hasNoSessions = product?.variants?.length === 0
  const hasNoFilteredSessions = !filteredVariants.length && !hasNoSessions

  const handleVariantClick = (variant: IProductVariant, added: boolean) => {
    let newSelectedVariants = [...selectedVariants]
    if(added){
        newSelectedVariants.push(variant.id.toString())
    }else{
        newSelectedVariants = newSelectedVariants.filter(v => v !== variant.id.toString())
    }
    setSelectedVariants(newSelectedVariants)
    onClick && onClick(newSelectedVariants, variant, added)
  }

  useEffect(() => {
    if(selectedVariantsValue){
        setSelectedVariants(selectedVariantsValue.map(v => v.id.toString()))
    }
  }, [selectedVariantsValue])
  

  return (
    <div id="react-product" className={className || ""}>
      <CalenderComponent 
        selectedDate={selectedDate} 
        track={track} 
        onChange={onDateChange} 
      />
      <div className="flex justify-center flex-col items-center gap-[8px] min-h-[300px] w-full mx-auto xl:mx-0">
        {loading ? (
          <div className="flex justify-center items-center h-[300px] w-full">
            <LoadingSpinner />
          </div>
        ) : (
          <div className="w-full rounded-[15px] bg-[#555758] relative flex-1">
            <FilterOptionsHeader
              filterOptions={filterOptions}
              handleFilterOptionClick={handleFilterOptionClick}
              getTotalAvailableSeats={getTotalAvailableSeats}
              track={track}
            />
            
            <SessionsContent
              hasNoSessions={hasNoSessions}
              hasNoFilteredSessions={hasNoFilteredSessions}
              filteredVariants={filteredVariants}
              gridConfig={gridConfig}
              track={track}
              selectedVariants={selectedVariants}
              onVariantClick={handleVariantClick}
              allSelectable={allSelectable}
            />
          </div>
        )}
      </div>
    </div>
  )
}

// Extracted components
const FilterOptionsHeader = ({ 
  filterOptions, 
  handleFilterOptionClick, 
  getTotalAvailableSeats, 
  track 
}: {
  filterOptions: any[]
  handleFilterOptionClick: (option: any) => () => void
  getTotalAvailableSeats: (filter: any) => number
  track: TBookingType
}) => (
  <div className="flex flex-row text-center gap-[5px] p-[5px] rounded-[15px] bg-[#393B41]">
    {filterOptions.map((option) => (
      <FilterBoxHeader
        key={option.filter as string}
        title={option.title}
        subTitle={"subTitle" in option ? option.subTitle : undefined}
        isActive={option.active}
        onClick={handleFilterOptionClick(option)}
        numAvailableSeats={getTotalAvailableSeats(option.filter)}
        track={track}
      />
    ))}
  </div>
)

const SessionsContent = ({ 
  hasNoSessions, 
  hasNoFilteredSessions, 
  filteredVariants, 
  gridConfig, 
  track,
  selectedVariants,
  onVariantClick,
  allSelectable
}: {
  hasNoSessions: boolean
  hasNoFilteredSessions: boolean
  filteredVariants: any[]
  gridConfig: string
  track: TBookingType
  selectedVariants: string[]
  onVariantClick: (variant: IProductVariant, added: boolean) => void
  allSelectable?: boolean
}) => {
  const emptyStateMessage = hasNoSessions 
    ? "Sessions not available yet"
    : "No sessions available for this time range"

  const handleClick = (variant: IProductVariant) => () =>{
    const isRemoved = selectedVariants.includes(variant.id.toString());
    onVariantClick(variant, !isRemoved)
  }

  if (hasNoSessions || hasNoFilteredSessions) {
    return (
      <p className="text-white text-center font-[600] py-[30px] lg:py-[0px] lg:absolute lg:top-[56%] lg:left-[50%] lg:-translate-y-1/2 lg:-translate-x-1/2">
        {emptyStateMessage}
      </p>
    )
  }

  return (
    <div className="py-[12px] px-[2px] md:px-[10px]">
      <div className={`grid gap-[1px] bg-[#555758] ${gridConfig}`}>
        {filteredVariants.map((variant) => (
          <VariantsBlock
            key={variant.id}
            variant={variant}
            isSelected={selectedVariants.includes(variant.id.toString())}
            activeRace={null}
            track={track}
            onClick={handleClick(variant)}
            allSelectable={allSelectable}
          />
        ))}
      </div>
    </div>
  )
}

const CalenderComponent = ({ 
  selectedDate, 
  track, 
  onChange 
}: { 
  selectedDate: Date
  track: TBookingType
  onChange: (date: Date) => void 
}) => {
  const [showDesktopCalendar, setShowDesktopCalendar] = useState<boolean>(false)
  const [tempSelectedDate, setTempSelectedDate] = useState<Date>(selectedDate || new Date())
  const calenderRef = useRef<HTMLDivElement | null>(null)
  const trackStyles = getTrackStyles(track)

  const formattedDateString = useMemo(() => formatDateString(selectedDate), [selectedDate])
  
  const dateDiff = useMemo(() => (
    selectedDate 
      ? selectedDate.getTime() 
      : new Date().getTime() - DateTime.local().startOf("day").toMillis()
  ), [selectedDate])

  const handleMonthNavigation = (direction: 'prev' | 'next') => {
    const offset = direction === 'prev' ? -1 : 1
    setTempSelectedDate(prev => adjustDateForMonth(prev, offset))
  }

  const handleDateSelect = (date: Date) => {
    setTempSelectedDate(date)
    setShowDesktopCalendar(false)
    onChange(date)
  }

  const handleOutsideClick = (event: MouseEvent) => {
    if (calenderRef.current && !calenderRef.current.contains(event.target as Node)) {
      setShowDesktopCalendar(false)
    }
  }

  useEffect(() => {
    selectedDate && setTempSelectedDate(selectedDate)
  }, [selectedDate])

  useEffect(() => {
    if (showDesktopCalendar) {
      document.addEventListener("mousedown", handleOutsideClick)
      return () => document.removeEventListener("mousedown", handleOutsideClick)
    }
  }, [showDesktopCalendar])

  return (
    <div className="mx-auto flex flex-col items-center grid-cols-1 xl:grid-cols-2">
      <div className="flex justify-center items-center text-white relative mb-4 bg-[#393B41] py-1 px-2 rounded-[15px]">
        <span className="font-[700] text-[20px] mr-3">
          {formattedDateString}
        </span>
        
        <CalendarIcon
          className="w-8 h-8 cursor-pointer text-[#9d62fe]"
          onClick={(e) => {
            e.stopPropagation()
            setShowDesktopCalendar(!showDesktopCalendar)
          }}
        />

        {showDesktopCalendar && (
          <CalendarDropdown
            calenderRef={calenderRef}
            tempSelectedDate={tempSelectedDate}
            trackStyles={trackStyles}
            dateDiff={dateDiff}
            onMonthNavigation={handleMonthNavigation}
            onDateSelect={handleDateSelect}
            onClose={() => setShowDesktopCalendar(false)}
          />
        )}
      </div>
    </div>
  )
}

const CalendarDropdown = ({
  calenderRef,
  tempSelectedDate,
  trackStyles,
  dateDiff,
  onMonthNavigation,
  onDateSelect,
  onClose
}: {
  calenderRef: React.RefObject<HTMLDivElement>
  tempSelectedDate: Date
  trackStyles: TrackStyles
  dateDiff: number
  onMonthNavigation: (direction: 'prev' | 'next') => void
  onDateSelect: (date: Date) => void
  onClose: () => void
}) => (
  <div
    className="absolute rounded-[15px] shadow-md z-50 top-[45px] left-1/2 transform -translate-x-1/2 bg-[#393B41]"
    ref={calenderRef}
  >
    <div className="w-full flex justify-center items-start">
      <div className="overflow-hidden rounded-[15px] w-full">
        <CalendarHeader
          tempSelectedDate={tempSelectedDate}
          trackStyles={trackStyles}
          dateDiff={dateDiff}
          onMonthNavigation={onMonthNavigation}
          onClose={onClose}
        />
        
        <Calendar
          className={`calender m-auto ${trackStyles.calendarClass}`}
          date={tempSelectedDate}
          onChange={onDateSelect}
          showMonthAndYearPickers={false}
          minDate={new Date()}
          weekStartsOn={1}
        />
      </div>
    </div>
  </div>
)

const CalendarHeader = ({
  tempSelectedDate,
  trackStyles,
  dateDiff,
  onMonthNavigation,
  onClose
}: {
  tempSelectedDate: Date
  trackStyles: TrackStyles
  dateDiff: number
  onMonthNavigation: (direction: 'prev' | 'next') => void
  onClose: () => void
}) => (
  <div className="bg-[#393B41] pt-[10px] pr-[10px] flex items-center justify-between px-[10px]">
    <span className={`font-[700] text-center text-white text-[20px] ${trackStyles.fontClass}`}>
      {tempSelectedDate.toLocaleString('default', { 
        month: 'long', 
        year: 'numeric' 
      })}
    </span>
    
    <div className="flex items-center">
      <ChevronLeftIcon
        className={`w-[30px] h-[30px] cursor-pointer text-[#9d62fe] ${
          dateDiff <= 0 ? "opacity-50 pointer-events-none" : ""
        }`}
        onClick={() => onMonthNavigation('prev')}
      />
      
      <ChevronRightIcon 
        className="w-[30px] h-[30px] cursor-pointer text-[#9d62fe] ml-2" 
        onClick={() => onMonthNavigation('next')} 
      />
      
      <XIcon 
        className="w-5 h-5 cursor-pointer text-white ml-2" 
        onClick={onClose} 
      />
    </div>
  </div>
)

interface FilterBoxHeaderProps {
  title: string
  subTitle?: string
  isActive: boolean
  onClick: () => void
  numAvailableSeats: number
  track?: TBookingType
}

const FilterBoxHeader: React.FC<FilterBoxHeaderProps> = ({ 
  title, 
  subTitle, 
  isActive, 
  onClick, 
  numAvailableSeats, 
  track 
}) => {
  const trackStyles = getTrackStyles(track || 'MAIN_TRACK')
  const isClickable = numAvailableSeats > 0 || isActive
  const availabilityText = numAvailableSeats === 0 ? "Sold Out" : `${numAvailableSeats} available`
  
  return (
    <div
      className={`flex-1 font-bold py-[5px] rounded-[10px] ${
        isClickable ? "cursor-pointer text-white" : "text-[#808080]"
      } ${
        isActive ? trackStyles.activeBackground : "bg-transparent"
      }`}
      onClick={() => isClickable && onClick()}
    >
      <div className={`text-[12px] md:text-[18px] font-[700] ${trackStyles.fontClass}`}>
        {title}
      </div>
      
      {subTitle && (
        <div className="text-[10px] md:text-[14px] font-[500]">
          {subTitle}
        </div>
      )}
      
      <div className="text-[10px] md:text-[14px] font-[500]">
        {availabilityText}
      </div>
    </div>
  )
}

export default BookingSelection